import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {DomainSetting} from '@gatewee/types/domain-setting';
import {RecipientSetting} from '@gatewee/types/recipient-setting';
import {PaymentProvider} from '@gatewee/types/provider';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';

interface DialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
}

@Component({
  selector: 'enabled-providers',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
  ],
  templateUrl: './enabled-providers.component.html',
})
export class EnabledProvidersComponent implements OnInit {
  formGroup!: FormGroup;
  submitting = false;
  id!: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;

  // All available payment providers
  availableProviders: PaymentProvider[] = Object.values(PaymentProvider);

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    this.id = this.context.data.id;
    this.setting = this.context.data.setting;
    this.editSettingFn = this.context.data.editSettingFn;
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    // Parse setting.value - should be an array of PaymentProvider strings
    let enabledProviders: PaymentProvider[] = [];

    if (this.setting.value) {
      try {
        const parsed =
          typeof this.setting.value === 'string'
            ? JSON.parse(this.setting.value)
            : this.setting.value;

        if (Array.isArray(parsed)) {
          enabledProviders = parsed as PaymentProvider[];
        } else if (typeof parsed === 'object' && parsed !== null) {
          // If it's an object, convert to array
          enabledProviders = Object.keys(parsed).filter(
            key => parsed[key] === true || parsed[key] === 'true',
          ) as PaymentProvider[];
        }
      } catch (e) {
        console.error('Error parsing enabled providers:', e);
      }
    }

    // Create form controls for each provider (checkbox)
    const formControls: Record<string, [boolean]> = {};
    this.availableProviders.forEach(provider => {
      formControls[provider] = [enabledProviders.includes(provider)];
    });

    this.formGroup = this.fb.group(formControls);
  }

  getProviderLabel(provider: PaymentProvider): string {
    return this.translate.instant(`enums.paymentProvider.${provider}`);
  }

  async onSubmit(): Promise<void> {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.submitting = true;

    // Get enabled providers from form values
    const formValue = this.formGroup.value;
    const enabledProviders: PaymentProvider[] = this.availableProviders.filter(
      provider => formValue[provider] === true,
    );

    // Create updated setting with array of enabled providers as JSON string
    const updatedSetting: RecipientSetting | DomainSetting = {
      ...this.setting,
      value: JSON.stringify(enabledProviders),
    };

    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: (error: Error) => {
        console.error('Error updating setting:', error);
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }
}
